import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import type { ModelMetaType } from '../../types/metaModel'
import type {
  TemplateVersion,
  TemplateVersionInfo,
} from '../../types/templateModel'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'

const templateDescendantIds = (forTemplate: TemplateVersionInfo): string[] => {
  const childDescendants = forTemplate.children.flatMap(templateDescendantIds)

  return [forTemplate.identifier, ...childDescendants]
}

const isTypeOrSubType = (
  parentCandidate: string | undefined,
  childType: ModelMetaType,
) => {
  const childHierarcy = [childType.templateType?.typeName ?? childType.typeName]
  let currentLevel = childType

  while (currentLevel.superType) {
    currentLevel = currentLevel.superType as ModelMetaType

    childHierarcy.push(
      currentLevel.templateType?.typeName ?? currentLevel.typeName,
    )
  }

  return !!parentCandidate && childHierarcy.includes(parentCandidate)
}

/**
 * Hooks used for resolving the list of templates that can be set as parents of
 * any given template. Validates the returned templates have the same model type as the
 * targeted template, and that they're not descendants of the template
 */
export const useTemplateParentOptions = (
  templateVersion: Optional<TemplateVersion>,
) => {
  const allDescendants = useMemo(
    () =>
      templateVersion ? templateDescendantIds(templateVersion.versionInfo) : [],
    [templateVersion],
  )
  const { applicationTemplates: applicationTemplateOperations } =
    useApplicationViewOperationsContext()
  const modelType = templateVersion?.modelType

  return useMemo(() => {
    if (!modelType) {
      return []
    }
    const operationsForParentCandidates = applicationTemplateOperations
      .values()
      .toArray()

    return operationsForParentCandidates
      .filter((createOperations) => {
        if (!createOperations.lastEntity) {
          return false
        }

        assertNotEmpty(createOperations.lastEntity.templateTypeName)

        const isOperationOfType = isTypeOrSubType(
          createOperations.lastEntity.templateTypeName,
          modelType,
        )

        return (
          createOperations.lastEntity.modelTypeName &&
          isOperationOfType &&
          !allDescendants.includes(createOperations.templateId)
        )
      })
      .map((createOperations) => createOperations.templateId)
      .sort((t1, t2) => t1.localeCompare(t2))
  }, [modelType, applicationTemplateOperations, allDescendants])
}
